/**
 * Template Manager Component
 * Demonstrates individual template operations
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTemplateApi } from '../hooks/useTemplateApi';
import { TemplateData } from '../services/templateApi';

const TemplateManager: React.FC = () => {
  const {
    templates,
    loading,
    error,
    lastOperation,
    addTemplate,
    getTemplates,
    updateTemplate,
    deleteTemplate,
    clearError,
    refreshTemplates
  } = useTemplateApi();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState<Partial<TemplateData>>({
    template_id: '',
    component_name: '',
    file_path: '',
    name: '',
    category: 'modern',
    industry: 'tech',
    style: 'professional',
    is_active: true
  });

  // Load templates on component mount
  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const handleAddTemplate = async () => {
    if (!newTemplate.template_id || !newTemplate.component_name || !newTemplate.file_path) {
      alert('Please fill in required fields: template_id, component_name, file_path');
      return;
    }

    try {
      await addTemplate(newTemplate as TemplateData);
      setNewTemplate({
        template_id: '',
        component_name: '',
        file_path: '',
        name: '',
        category: 'modern',
        industry: 'tech',
        style: 'professional',
        is_active: true
      });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add template:', err);
    }
  };

  const handleUpdateTemplate = async (templateId: string, updates: Partial<TemplateData>) => {
    try {
      await updateTemplate(templateId, updates);
    } catch (err) {
      console.error('Failed to update template:', err);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate(templateId);
      } catch (err) {
        console.error('Failed to delete template:', err);
      }
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Template Manager</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gérez vos templates CV - Ajoutez, modifiez ou supprimez des templates
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <span>+</span>
          {showAddForm ? 'Annuler' : 'Nouveau Template'}
        </button>
      </div>

      {/* Status Display */}
      {lastOperation && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md"
        >
          <p className="text-sm text-blue-700">{lastOperation}</p>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
        >
          <div className="flex justify-between items-center">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Template Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-gray-50 rounded-md"
        >
          <h3 className="text-lg font-semibold mb-4">➕ Créer un nouveau template</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID du Template *
              </label>
              <input
                type="text"
                value={newTemplate.template_id || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, template_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: modern_tech_003"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du Composant *
              </label>
              <input
                type="text"
                value={newTemplate.component_name || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, component_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: ModernTech003"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chemin du Fichier *
              </label>
              <input
                type="text"
                value={newTemplate.file_path || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, file_path: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: /templates/modern/ModernTech003.jsx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du Template
              </label>
              <input
                type="text"
                value={newTemplate.name || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: Modern Tech Professional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie
              </label>
              <select
                value={newTemplate.category || 'modern'}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="modern">Moderne</option>
                <option value="classic">Classique</option>
                <option value="creative">Créatif</option>
                <option value="minimal">Minimal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secteur d'Activité
              </label>
              <select
                value={newTemplate.industry || 'tech'}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, industry: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="tech">Technologie</option>
                <option value="business">Business</option>
                <option value="creative">Créatif</option>
                <option value="academic">Académique</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddTemplate}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? '⏳ Création...' : '✅ Créer Template'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              ❌ Annuler
            </button>
          </div>
        </motion.div>
      )}

      {/* Templates List */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">📄 Templates ({templates.length})</h3>
          <button
            onClick={refreshTemplates}
            disabled={loading}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors flex items-center gap-1"
          >
            {loading ? '⏳ Chargement...' : '🔄 Actualiser'}
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📄</div>
            <p>Aucun template trouvé. Créez votre premier template !</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              ➕ Créer le premier template
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {templates.map((template) => (
              <motion.div
                key={template.template_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border border-gray-200 rounded-md hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {template.name || template.template_id}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {template.component_name} • {template.category} • {template.industry}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{template.file_path}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateTemplate(template.template_id, { 
                        is_active: !template.is_active 
                      })}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        template.is_active 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {template.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.template_id)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManager;
